import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Plus, Calendar, Clock, X } from 'lucide-react';
import { SubTaskFormData } from '../../types/task';

interface SubTaskFormProps {
  onAdd: (subtask: SubTaskFormData) => void;
}

const initialSubtaskState: SubTaskFormData = {
  title: '',
  description: '',
  dueDate: '',
  dueTime: '',
  hasDueDate: false,
};

export const SubTaskForm: React.FC<SubTaskFormProps> = ({ onAdd }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [subtask, setSubtask] = useState<SubTaskFormData>(initialSubtaskState);

  const handleSubmit = () => {
    if (!subtask.title.trim()) return;
    
    onAdd(subtask);
    setSubtask(initialSubtaskState);
    setIsAdding(false);
  };

  if (!isAdding) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsAdding(true)}
        icon={<Plus className="w-4 h-4" />}
        className="w-full"
      >
        Add Subtask
      </Button>
    );
  }

  return (
    <Card className="p-4 bg-gray-50">
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-medium">Add Subtask</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            icon={<X className="w-4 h-4" />}
            onClick={() => {
              setSubtask(initialSubtaskState);
              setIsAdding(false);
            }}
          />
        </div>

        <input
          type="text"
          value={subtask.title || ''}
          onChange={(e) => setSubtask(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Subtask title"
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          required
        />

        <textarea
          value={subtask.description || ''}
          onChange={(e) => setSubtask(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Description (optional)"
          className="w-full px-3 py-2 border rounded-md h-20 focus:ring-2 focus:ring-blue-500"
        />

        <div className="flex items-center gap-2 mb-4">
          <button
            type="button"
            onClick={() => setSubtask(prev => ({ ...prev, hasDueDate: !prev.hasDueDate }))}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
              subtask.hasDueDate
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-gray-200 text-gray-600 hover:border-blue-200'
            }`}
          >
            <Calendar className="w-4 h-4" />
            <span className="text-sm font-medium">Due Date</span>
          </button>
        </div>

        {subtask.hasDueDate && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline-block mr-1" />
                Due Date
              </label>
              <input
                type="date"
                value={subtask.dueDate || ''}
                onChange={(e) => setSubtask(prev => ({ ...prev, dueDate: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                required={subtask.hasDueDate}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="w-4 h-4 inline-block mr-1" />
                Due Time
              </label>
              <input
                type="time"
                value={subtask.dueTime || ''}
                onChange={(e) => setSubtask(prev => ({ ...prev, dueTime: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                required={subtask.hasDueDate}
              />
            </div>
          </div>
        )}

        <Button
          type="button"
          variant="primary"
          className="w-full"
          onClick={handleSubmit}
          disabled={!subtask.title.trim()}
        >
          Add Subtask
        </Button>
      </div>
    </Card>
  );
};